import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { TicketStatusBadge } from "@/components/TicketStatusBadge";
import { api, apiErrorMessage } from "@/lib/api";
import { Loader2, Send, UserRound, FileText } from "lucide-react";

const TICKET_STATUSES = ["open", "in_progress", "resolved", "closed"];

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

export const AdminSupportTab = ({ onUnreadChange }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const threadEndRef = useRef(null);

  const loadTickets = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/tickets");
      setTickets(data.tickets);
      return data.tickets;
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to load support tickets."));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const openThread = useCallback(async (ticketId) => {
    setSelectedId(ticketId);
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      setThread(data);
      onUnreadChange?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to load the ticket thread."));
    } finally {
      setThreadLoading(false);
    }
  }, [onUnreadChange]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread]);

  const filtered = filter === "all" ? tickets : tickets.filter((ticket) => ticket.status === filter);

  const sendReply = async () => {
    if (!selectedId || reply.trim().length < 2) return;
    setSending(true);
    try {
      await api.post(`/tickets/${selectedId}/messages`, { body: reply });
      setReply("");
      toast.success("Reply sent. The member has been notified and emailed.");
      await openThread(selectedId);
      loadTickets();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Reply failed to send."));
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (status) => {
    if (!selectedId) return;
    try {
      await api.patch(`/admin/tickets/${selectedId}`, { status });
      toast.success(`Ticket marked ${status.replace("_", " ")}.`);
      await openThread(selectedId);
      loadTickets();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Status update failed."));
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
      <Card className="bg-card border-border h-fit">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="font-display text-lg">Ticket queue</CardTitle>
              <CardDescription>{filtered.length} ticket{filtered.length === 1 ? "" : "s"}</CardDescription>
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-8 w-32" data-testid="admin-ticket-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {TICKET_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <>
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="admin-tickets-empty">
              No tickets in this view.
            </p>
          ) : (
            <ScrollArea className="max-h-[520px]">
              <div className="space-y-2 pr-2">
                {filtered.map((ticket) => (
                  <button
                    key={ticket.id}
                    type="button"
                    data-testid={`admin-ticket-item-${ticket.id}`}
                    onClick={() => openThread(ticket.id)}
                    className={`w-full rounded-md border p-3 text-left transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      selectedId === ticket.id ? "border-primary/50 bg-secondary/70" : "border-border bg-background"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{ticket.subject}</p>
                      <TicketStatusBadge status={ticket.status} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <UserRound className="h-3 w-3" /> {ticket.user_email} · {formatWhen(ticket.updated_at)}
                    </p>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border flex flex-col">
        {!selectedId ? (
          <CardContent className="flex flex-1 items-center justify-center py-16">
            <p className="text-sm text-muted-foreground" data-testid="admin-thread-placeholder">
              Select a ticket from the queue to reply.
            </p>
          </CardContent>
        ) : threadLoading || !thread ? (
          <CardContent className="space-y-3 py-6">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        ) : (
          <>
            <CardHeader className="pb-3 border-b border-border">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="font-display text-lg" data-testid="admin-thread-subject">
                    {thread.ticket.subject}
                  </CardTitle>
                  <CardDescription className="capitalize">
                    {thread.ticket.user_email} · {thread.ticket.category} · opened {formatWhen(thread.ticket.created_at)}
                  </CardDescription>
                </div>
                <Select value={thread.ticket.status} onValueChange={updateStatus}>
                  <SelectTrigger className="h-8 w-36" data-testid="admin-ticket-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TICKET_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.replace("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {thread.order_summary && (
                <div
                  className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-primary/25 bg-primary/5 px-3 py-2"
                  data-testid="admin-linked-order-chip"
                >
                  <FileText className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-medium">
                    Linked order <span className="font-mono">{thread.order_summary.id.slice(0, 8)}</span>
                  </span>
                  <span className="text-xs text-muted-foreground capitalize">
                    {String(thread.order_summary.request_type || "").replaceAll("_", " ")} ·{" "}
                    <span className="font-mono">{thread.order_summary.identifier_value}</span> ·{" "}
                    verification {String(thread.order_summary.verification_status || "").replaceAll("_", " ")} ·{" "}
                    ops {String(thread.order_summary.admin_status || "").replaceAll("_", " ")} ·{" "}
                    {thread.order_summary.report_paid ? "paid" : "unpaid"}
                  </span>
                </div>
              )}
            </CardHeader>
            <CardContent className="flex-1 pt-4">
              <ScrollArea className="max-h-[380px]">
                <div className="space-y-3 pr-2" data-testid="admin-thread-messages">
                  {thread.messages.map((msg) => {
                    const fromAdmin = msg.author_role === "admin";
                    return (
                      <div key={msg.id} className={`flex ${fromAdmin ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[85%] rounded-md border p-3 ${
                            fromAdmin ? "border-primary/30 bg-primary/10" : "border-border bg-secondary/60"
                          }`}
                        >
                          <p className="text-[11px] text-muted-foreground mb-1">
                            {fromAdmin ? "Support team" : msg.author_email} · {formatWhen(msg.created_at)}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={threadEndRef} />
                </div>
              </ScrollArea>
            </CardContent>
            <div className="border-t border-border p-4 flex items-end gap-2">
              <Textarea
                data-testid="admin-reply-input"
                placeholder="Reply to the member… (they get an email + in-app notification)"
                className="min-h-[64px] resize-none"
                value={reply}
                onChange={(event) => setReply(event.target.value)}
              />
              <Button
                data-testid="admin-reply-send-button"
                onClick={sendReply}
                disabled={sending || reply.trim().length < 2}
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};
